import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { LoaderCircleIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 10;

export type DataTableProps<TData, TValue> = {
  columns: Array<ColumnDef<TData, TValue>>;
  data: TData[];
  onRowClick?: (row: TData) => void;
};

export function DataTable<TData, TValue>({ columns, data, onRowClick }: DataTableProps<TData, TValue>) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    state: { columnFilters },
  });

  const allRows = table.getRowModel().rows;
  const visibleRows = allRows.slice(0, visibleCount);
  const hasMore = visibleCount < allRows.length;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore) {
          setVisibleCount(prev => prev + PAGE_SIZE);
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore]);

  // 필터 변경 시 visibleCount 초기화
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [columnFilters]);

  return (
    <div className="space-y-4">
      {/* 테이블 */}
      <Table className="sm:table block rounded-lg">
        {/* 테이블 헤더 */}
        <TableHeader className="hidden sm:table-header-group">
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                const isFirst = index === 0;
                const isLast = index === headerGroup.headers.length - 1;
                const width = isFirst ? "120px" : isLast ? "100px" : undefined;

                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      width && `w-[${width}] text-center`,
                      "font-bold h-12 sticky top-0 z-10 bg-muted text-foreground",
                    )}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>

        {/* 테이블 바디 */}
        <TableBody className="block sm:table-row-group space-y-2 sm:space-y-0">
          {visibleRows?.map(row => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
              className="relative flex flex-wrap flex-col sm:flex-row items-center sm:table-row rounded-lg border-0 sm:border-b px-4 pt-4 sm:p-0 bg-card sm:bg-transparent mb-4 sm:mb-0 cursor-pointer hover:bg-accent/60 active:scale-[0.98] transition-transform sm:active:scale-100"
              onClick={() => onRowClick?.(row.original)}
            >
              {row.getVisibleCells().map((cell, index) => {
                const isFirst = index === 0;
                const isLast = index === row.getVisibleCells().length - 1;
                const width = isFirst ? "120px" : isLast ? "100px" : undefined;

                return (
                  <TableCell
                    key={cell.id}
                    className={cn(width && `w-[${width}] text-center`, "flex-1 min-w-0 font-bold")}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* 로딩 아이콘 */}
      <div ref={sentinelRef} className="flex items-center justify-center text-muted-foreground">
        {hasMore && <LoaderCircleIcon className="w-4 h-4 animate-spin" />}
      </div>
    </div>
  );
}
